import type {
  CoinstoreDepthResponse,
  CoinstoreKlinesResponse,
  CoinstorePriceResponse,
  CoinstoreSymbolsResponse,
  CoinstoreTickersResponse,
  CoinstoreTradesResponse,
} from './types';

import { get, post } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.coinstore.com/api';

/**
 * Coinstore REST API endpoints.
 * @see https://coinstore-openapi.github.io/en/
 */
export const coinstoreRestEndpoints = {
  tickers: `${BASE_URL}/v1/market/tickers`,
  depth: `${BASE_URL}/v1/market/depth`,
  klines: `${BASE_URL}/v1/market/kline`,
  trades: `${BASE_URL}/v1/market/trade`,
  symbols: `${BASE_URL}/v2/public/config/spot/symbols`,
  price: `${BASE_URL}/v1/ticker/price`,
} as const;

// ========================================
// Helper Functions
// ========================================

/**
 * Format symbol for Coinstore API calls.
 * Coinstore uses uppercase concatenated format: "BTCUSDT", "ETHUSDT".
 *
 * @example
 * formatCoinstoreSymbol("BTC", "USDT") // "BTCUSDT"
 */
export function formatCoinstoreSymbol(base: string, quote: string): string {
  return `${base}${quote}`.toUpperCase();
}

/**
 * Parse Coinstore symbol into base/quote.
 * Coinstore symbols don't have a separator, so we need known quote currencies.
 * Common quote currencies: USDT, USDC, BTC, ETH
 *
 * @example
 * parseCoinstoreSymbol("BTCUSDT") // { base: "BTC", quote: "USDT" }
 * parseCoinstoreSymbol("ETHBTC") // { base: "ETH", quote: "BTC" }
 */
export function parseCoinstoreSymbol(symbol: string): { base: string; quote: string } | null {
  const upperSymbol = symbol.toUpperCase();

  // Common quote currencies in order of priority (most specific first)
  const quoteCurrencies = ['USDT', 'USDC', 'BUSD', 'BTC', 'ETH', 'BNB', 'USD'];

  for (const quote of quoteCurrencies) {
    if (upperSymbol.endsWith(quote)) {
      const base = upperSymbol.slice(0, -quote.length);
      if (base.length > 0) {
        return { base, quote };
      }
    }
  }

  return null;
}

/**
 * Validate Coinstore REST API response.
 * Throws error if response code is not 0.
 */
export function validateCoinstoreResponse<T extends { code: number | string; message?: string }>(
  response: T,
  operation: string,
): void {
  // Handle both string "0" and number 0
  if (response.code != 0) {
    throw new Error(
      `Coinstore ${operation} failed: code=${response.code}, message=${response.message || 'unknown'}`,
    );
  }
}

// ========================================
// Tickers Endpoint
// ========================================

/**
 * Fetches 24-hour ticker statistics for all symbols.
 * Coinstore Ticker API returns all tickers at once.
 * @see https://coinstore-openapi.github.io/en/#ticker
 */
export const getCoinstoreTickers = async (
  options?: RestRequestOptions,
): Promise<CoinstoreTickersResponse> => {
  const response = await get<CoinstoreTickersResponse>({
    url: coinstoreRestEndpoints.tickers,
    options,
  });

  validateCoinstoreResponse(response.data, 'getCoinstoreTickers');

  return response.data;
};

// ========================================
// Symbols Endpoint
// ========================================

/**
 * Request parameters for getCoinstoreSymbols.
 */
export interface CoinstoreSymbolsRequest {
  /** Symbol codes to filter (optional) */
  symbolCodes?: string[];
  /** Symbol IDs to filter (optional) */
  symbolIds?: number[];
}

/**
 * Fetches symbol information including instrumentId mapping.
 * This is essential for WebSocket subscriptions which use numeric instrumentId.
 *
 * @see https://coinstore-openapi.github.io/en/#currency-spot-information
 */
export const getCoinstoreSymbols = async (
  params?: CoinstoreSymbolsRequest,
  options?: RestRequestOptions,
): Promise<CoinstoreSymbolsResponse> => {
  const body: Record<string, unknown> = {};

  if (params?.symbolCodes) body.symbolCodes = params.symbolCodes;
  if (params?.symbolIds) body.symbolIds = params.symbolIds;

  const response = await post<CoinstoreSymbolsResponse>({
    url: coinstoreRestEndpoints.symbols,
    body: Object.keys(body).length > 0 ? body : {},
    options,
  });
  // Symbols endpoint returns code as string "0"
  if (String(response.data.code) !== '0') {
    throw new Error(
      `Coinstore getCoinstoreSymbols failed: code=${response.data.code}, message=${response.data.message}`,
    );
  }

  return response.data;
};

// ========================================
// Depth (Orderbook) Endpoint
// ========================================

/**
 * Request parameters for getCoinstoreDepth.
 */
export interface CoinstoreDepthRequest {
  /** Trading pair symbol (e.g., "BTCUSDT") */
  symbol: string;
  /** Depth level: 5, 10, 20, 50, 100 (default: 20) */
  depth?: 5 | 10 | 20 | 50 | 100;
}

/**
 * Fetches orderbook depth (snapshot) for a symbol.
 * Returns bids and asks as [price, quantity, direction] tuples.
 *
 * @see https://coinstore-openapi.github.io/en/#get-depth
 */
export const getCoinstoreDepth = async (
  params: CoinstoreDepthRequest,
  options?: RestRequestOptions,
): Promise<CoinstoreDepthResponse> => {
  const { symbol, depth = 20 } = params;

  const response = await get<CoinstoreDepthResponse>({
    url: `${coinstoreRestEndpoints.depth}/${symbol}`,
    params: { depth },
    options,
  });

  validateCoinstoreResponse(response.data, 'getCoinstoreDepth');

  return response.data;
};

// ========================================
// Klines (Candlesticks) Endpoint
// ========================================

/**
 * Coinstore supported kline intervals.
 * @see https://coinstore-openapi.github.io/en/#get-k-line
 */
export type CoinstoreKlineInterval =
  | '1min'
  | '5min'
  | '15min'
  | '30min'
  | '60min'
  | '4hour'
  | '12hour'
  | '1day'
  | '1week';

/**
 * Request parameters for getCoinstoreKlines.
 */
export interface CoinstoreKlinesRequest {
  /** Trading pair symbol (e.g., "BTCUSDT") */
  symbol: string;
  /** Kline interval (default: "1min") */
  period?: CoinstoreKlineInterval;
  /** Number of candles to return: 1-2000 (default: 20) */
  size?: number;
}

/**
 * Fetches historical klines (candlesticks) from Coinstore.
 * Returns candles with open/high/low/close prices and volume.
 *
 * @see https://coinstore-openapi.github.io/en/#get-k-line
 */
export const getCoinstoreKlines = async (
  params: CoinstoreKlinesRequest,
  options?: RestRequestOptions,
): Promise<CoinstoreKlinesResponse> => {
  const { symbol, period = '1min', size = 200 } = params;

  const response = await get<CoinstoreKlinesResponse>({
    url: `${coinstoreRestEndpoints.klines}/${symbol}`,
    params: { period, size },
    options,
  });

  validateCoinstoreResponse(response.data, 'getCoinstoreKlines');

  return response.data;
};

// ========================================
// Trades Endpoint
// ========================================

/**
 * Request parameters for getCoinstoreTrades.
 */
export interface CoinstoreTradesRequest {
  /** Trading pair symbol (e.g., "BTCUSDT") */
  symbol: string;
  /** Number of trades to return: 1-100 (default: 50) */
  size?: number;
}

/**
 * Fetches recent public trades for a symbol.
 * Returns trades with price, volume, time, and side.
 *
 * @see https://coinstore-openapi.github.io/en/#latest-trades
 */
export const getCoinstoreTrades = async (
  params: CoinstoreTradesRequest,
  options?: RestRequestOptions,
): Promise<CoinstoreTradesResponse> => {
  const { symbol, size = 50 } = params;

  const response = await get<CoinstoreTradesResponse>({
    url: `${coinstoreRestEndpoints.trades}/${symbol}`,
    params: { size },
    options,
  });

  validateCoinstoreResponse(response.data, 'getCoinstoreTrades');

  return response.data;
};

// ========================================
// Price Endpoint
// ========================================

/**
 * Request parameters for getCoinstorePrice.
 */
export interface CoinstorePriceRequest {
  /** Comma-separated list of symbols (optional, returns all if not specified) */
  symbol?: string;
}

/**
 * Fetches the latest price of all or specified symbols.
 *
 * @see https://coinstore-openapi.github.io/en/#get-the-latest-price-of-all-symbols
 */
export const getCoinstorePrice = async (
  params?: CoinstorePriceRequest,
  options?: RestRequestOptions,
): Promise<CoinstorePriceResponse> => {
  let url: string = coinstoreRestEndpoints.price;

  // Coinstore uses semicolon for query params in price endpoint
  if (params?.symbol) {
    url = `${url};symbol=${params.symbol}`;
  }

  const response = await get<CoinstorePriceResponse>({
    url,
    options,
  });

  validateCoinstoreResponse(response.data, 'getCoinstorePrice');

  return response.data;
};
